import { queryOptions } from '@tanstack/react-query'
import { z } from 'zod'

import { api } from '@/shared/api/http'
import {
  PAGE_MAX_LIMIT,
  type CostCenterOrderBy,
  type CostCenterOut,
  type Page,
} from '@/shared/api/types'

export const costCenterKeys = {
  all: () => ['cost-centers'] as const,
  lists: () => [...costCenterKeys.all(), 'list'] as const,
  list: (p: CostCenterListParams) => [...costCenterKeys.lists(), p] as const,
} as const

export interface CostCenterListParams {
  /** Casa por substring, sem diferenciar caixa, no nome. */
  readonly search?: string
  readonly order_by?: CostCenterOrderBy
  readonly limit?: number
  readonly offset?: number
}

/**
 * Centros de custo — serve DOIS consumidores com necessidades opostas.
 *
 * Sem params: carrega tudo de uma vez (é o seletor de filtro/agendamento; paginar
 * um dropdown é pior que buscar 1000 linhas de uma lista que raramente passa de
 * dezenas). Com params: a tela de Configurações, que filtra, ordena e pagina.
 *
 * A chave inclui os params, então os dois usos não se sobrescrevem no cache — e
 * `costCenterKeys.lists()` continua invalidando ambos por ser prefixo.
 */
export const costCentersQueryOptions = (p: CostCenterListParams = { limit: PAGE_MAX_LIMIT }) =>
  queryOptions({
    queryKey: costCenterKeys.list(p),
    queryFn: ({ signal }) =>
      api.get<Page<CostCenterOut>>(
        '/v1/cost-centers',
        { ...p, limit: p.limit ?? PAGE_MAX_LIMIT },
        signal,
      ),
    staleTime: 5 * 60_000,
  })

/** CostCenterCreate = {name} — só isso. */
export const costCenterFormSchema = z.object({
  name: z.string().trim().min(1, 'validation:required').max(120, 'validation:maxLength'),
})

export type CostCenterFormValues = z.infer<typeof costCenterFormSchema>

/** POST /v1/cost-centers — cadastro, sem Idempotency-Key. */
export function createCostCenter(v: CostCenterFormValues): Promise<CostCenterOut> {
  return api.post<CostCenterOut>('/v1/cost-centers', { body: { name: v.name } }).then((r) => r.data)
}

/** PATCH /v1/cost-centers/{id} — só `name`; edição de cadastro, sem Idempotency-Key. */
export function updateCostCenter(id: string, v: CostCenterFormValues): Promise<CostCenterOut> {
  return api.patch<CostCenterOut>(`/v1/cost-centers/${id}`, { name: v.name })
}

/**
 * DELETE /v1/cost-centers/{id} — SOFT delete.
 * 409 `cost_center_in_use` se vinculado a agendamento aberto: a tela orienta.
 */
export function deleteCostCenter(id: string): Promise<void> {
  return api.del(`/v1/cost-centers/${id}`)
}
